import { Injectable } from '@angular/core';
import { IExportStopRow, IRouteDetails, IRouteImportResult } from 'src/app/models';
import { FeatureType, ImportExportType } from 'src/app/models/enums';
import { LiteralService } from '../literal/literal.service';
import { UtilsService } from '../utils/utils.service';
import { RouteFormatConverter } from './route-format-converter';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const GEOJSON_MIME = 'application/geo+json';
const KML_MIME = 'application/vnd.google-earth.kml+xml';
const KMZ_MIME = 'application/vnd.google-earth.kmz';

const ACCEPT: Record<ImportExportType, string> = {
  [ImportExportType.STOPS]: '.xlsx',
  [ImportExportType.GEOJSON]: '.geojson,.json',
  [ImportExportType.KML]: '.kml',
  [ImportExportType.KMZ]: '.kmz',
};

const SCHEMA_KEYS = ['id', 'radius', 'direction', 'weight', 'distance', 'start', 'end', 'inflexion', 'demand', 'merge', 'bidirectional', 'alternatives'];

@Injectable({ providedIn: 'root' })
export class RouteTransferService {

  constructor(
    private converter: RouteFormatConverter,
    private utils: UtilsService,
    private literalService: LiteralService,
  ) {}

  fileAccept(type: ImportExportType): string {
    return ACCEPT[type];
  }

  async exportRoute(type: ImportExportType, map: google.maps.Map, route?: IRouteDetails): Promise<void> {
    const geojson = this.filterRouteFeatures(await this.readMapGeojson(map));
    const base = this.baseFilename(route);

    switch (type) {
      case ImportExportType.STOPS: {
        const rows = geojson.features.filter(isStop).map(toStopRow);
        const blob = await this.converter.stopsToXlsx(rows);
        this.utils.downloadBlob(blob, `${base}-${this.stopsSuffix()}.xlsx`, XLSX_MIME);
        break;
      }
      case ImportExportType.GEOJSON:
        this.utils.downloadBlob(JSON.stringify(geojson), `${base}.json`, GEOJSON_MIME);
        break;
      case ImportExportType.KML: {
        const kml = await this.converter.geojsonToKml(geojson);
        this.utils.downloadBlob(kml, `${base}.kml`, KML_MIME);
        break;
      }
      case ImportExportType.KMZ: {
        const blob = await this.converter.geojsonToKmz(geojson);
        this.utils.downloadBlob(blob, `${base}.kmz`, KMZ_MIME);
        break;
      }
    }
  }

  async parseImport(type: ImportExportType, file: File): Promise<IRouteImportResult> {
    if (type === ImportExportType.STOPS) {
      const buffer = await this.utils.readFileAsArrayBuffer(file);
      const rows = await this.converter.xlsxToStops(buffer);
      return { replace: false, plainStops: rows };
    }

    let fc: any;
    if (type === ImportExportType.GEOJSON) fc = JSON.parse(await this.utils.readFileAsText(file));
    else if (type === ImportExportType.KML) fc = await this.converter.kmlToGeojson(await this.utils.readFileAsText(file));
    else fc = await this.converter.kmzToGeojson(await this.utils.readFileAsArrayBuffer(file));

    return this.categorize(fc);
  }

  private categorize(fc: any): IRouteImportResult {
    const geojson: any[] = [];
    const plainStops: IExportStopRow[] = [];

    for (const raw of (fc?.features ?? [])) {
      const f = this.decodeFeature(raw);
      const geom = f.geometry?.type;
      const props = f.properties ?? {};
      if (geom === 'Point') {
        if (props.type === FeatureType.STOP) {
          geojson.push(f);
        } else {
          plainStops.push({ name: props.name ?? props.Name ?? '', latitude: f.geometry.coordinates[1], longitude: f.geometry.coordinates[0] });
        }
      } else if (geom === 'LineString' && props.type === FeatureType.SEGMENT && props.start && props.end) {
        geojson.push(f);
      }
    }
    return { replace: true, geojson, plainStops };
  }

  private decodeFeature(f: any): any {
    const props = { ...(f?.properties ?? {}) };
    for (const key of SCHEMA_KEYS) {
      if (typeof props[key] === 'string') {
        try { props[key] = JSON.parse(props[key]); } catch { /* keep the raw string */ }
      }
    }
    const id = typeof props.id === 'number' ? props.id : Number(f?.id);
    return { ...f, id: isNaN(id) ? f?.id : id, properties: props };
  }

  private readMapGeojson(map: google.maps.Map): Promise<any> {
    return new Promise((resolve) => map.data.toGeoJson((json: any) => resolve(this.utils.cleanTerrainObject(json))));
  }

  private filterRouteFeatures(fc: any): { type: 'FeatureCollection'; features: any[] } {
    const features = (fc?.features ?? []).filter((f: any) => {
      const t = f?.properties?.type;
      return t === FeatureType.STOP || t === FeatureType.SEGMENT;
    });
    return { type: 'FeatureCollection', features };
  }

  private baseFilename(route?: IRouteDetails): string {
    const name = (route?.name ?? 'route').trim() || 'route';
    return name.replace(/[\\/:*?"<>|]+/g, '_');
  }

  private stopsSuffix(): string {
    const label = this.literalService.get(`services.routes.design.importExportTypes.${ImportExportType.STOPS}`);
    return (label || 'stops').toLowerCase().replace(/[\\/:*?"<>|\s]+/g, '_');
  }
}

function isStop(feature: any): boolean {
  return feature?.properties?.type === FeatureType.STOP;
}

function toStopRow(feature: any): IExportStopRow {
  return {
    name: feature?.properties?.name ?? '',
    latitude: feature.geometry.coordinates[1],
    longitude: feature.geometry.coordinates[0],
  };
}
